import styled, { css } from 'styled-components';
import {
  MODE_BADGE_SIZE_TOKENS,
  MODE_BADGE_VARIANT_FLAT,
  MODE_BADGE_VARIANT_PILL,
  type ModeBadgeSize,
  type ModeBadgeTone,
  type ModeBadgeVariant,
} from './ModeBadge.constants';

const TONE_COLORS: Record<
  ModeBadgeTone,
  (theme: { colors?: Record<string, string | undefined> }) => {
    border: string;
    fg: string;
    dot: string;
  }
> = {
  neutral: ({ colors = {} }) => ({
    border: colors.outlineVariant ?? '#464555',
    fg: colors.onSurfaceVariant ?? '#C7C4D7',
    dot: colors.onSurfaceVariant ?? '#C7C4D7',
  }),
  primary: ({ colors = {} }) => {
    const fg = colors.primary ?? '#C1C1FF';
    return { border: `color-mix(in oklch, ${fg} 50%, transparent)`, fg, dot: fg };
  },
  success: ({ colors = {} }) => {
    const fg = colors.success ?? '#8CD88C';
    return { border: `color-mix(in oklch, ${fg} 50%, transparent)`, fg, dot: fg };
  },
  caution: ({ colors = {} }) => {
    const fg = colors.caution ?? '#FFDF9E';
    return { border: `color-mix(in oklch, ${fg} 50%, transparent)`, fg, dot: fg };
  },
  error: ({ colors = {} }) => {
    const fg = colors.error ?? '#FFB4AB';
    return { border: `color-mix(in oklch, ${fg} 50%, transparent)`, fg, dot: fg };
  },
  info: ({ colors = {} }) => {
    const fg = colors.primary ?? '#C1C1FF';
    return { border: `color-mix(in oklch, ${fg} 50%, transparent)`, fg, dot: fg };
  },
};

interface RootProps {
  $tone: ModeBadgeTone;
  $size: ModeBadgeSize;
  $variant: ModeBadgeVariant;
  $interactive: boolean;
}

/**
 * Two visual treatments share one component:
 *
 *   - `pill`: tinted BORDER over a transparent fill, full pill radius,
 *     sentence-case label, tabular numerals — the "high-emphasis chip"
 *     read used in page banners. Visually distinct from `StatusPill`,
 *     which uses a tinted FILL with no border.
 *   - `flat`: no border, transparent fill, near-square 8px radius,
 *     UPPERCASE letter-spaced label that picks up the tone colour —
 *     the "lives inside another toolbar" read used by the floating
 *     canvas toolbar (see `.ctb__mode` in the prototype).
 *
 * Same tone vocabulary so callers can swap one for the other freely.
 */
export const Root = styled.span<RootProps>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  vertical-align: middle;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  background: transparent;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  font-family: inherit;

  ${({ $variant, theme: { borderRadius = {} } }) =>
    $variant === MODE_BADGE_VARIANT_PILL
      ? css`
          border: 1px solid;
          border-radius: ${borderRadius.full ?? '100px'};
          letter-spacing: 0.01em;
          text-transform: none;
        `
      : css`
          border: 0;
          border-radius: 8px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        `}

  ${({ $size, $variant }) => {
    const tokens = MODE_BADGE_SIZE_TOKENS[$size];
    /* `flat` uses a slightly tighter type so the letter-spaced caps
       still fit the toolbar pill's vertical rhythm: 11/11.5px instead
       of 11.5/12.5px in `pill`. The 28px height comes from the
       prototype's `.ctb__mode` and reads visually equivalent to the
       neighbouring 32px IconButton circles. */
    const height = $variant === MODE_BADGE_VARIANT_FLAT ? '28px' : tokens.height;
    const fontSize =
      $variant === MODE_BADGE_VARIANT_FLAT
        ? $size === 'sm'
          ? '11px'
          : '11.5px'
        : tokens.fontSize;
    return css`
      height: ${height};
      padding: 0 ${tokens.padX};
      font-size: ${fontSize};
      gap: ${tokens.gap};
    `;
  }}

  ${({ $tone, $variant, theme }) => {
    const palette = TONE_COLORS[$tone]?.(theme) ?? TONE_COLORS.neutral(theme);
    return $variant === MODE_BADGE_VARIANT_PILL
      ? css`
          border-color: ${palette.border};
          color: ${palette.fg};
        `
      : css`
          color: ${palette.fg};
        `;
  }}

  ${({ $interactive, theme: { motion } }) =>
    $interactive &&
    css`
      cursor: pointer;
      transition: background ${motion?.duration?.fast ?? '120ms'}
        ${motion?.easing?.standard ?? 'cubic-bezier(0.2,0,0,1)'};
      &:hover {
        background: color-mix(in oklch, currentColor 12%, transparent);
      }
      &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }
    `}

  & > svg,
  & > .material-symbols-rounded {
    ${({ $variant }) =>
      $variant === MODE_BADGE_VARIANT_PILL
        ? css`
            width: 14px;
            height: 14px;
            font-size: 14px;
          `
        : css`
            width: 18px;
            height: 18px;
            font-size: 18px;
          `}
  }
`;

export const Dot = styled.span<{ $tone: ModeBadgeTone }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  ${({ $tone, theme }) => {
    const palette = TONE_COLORS[$tone]?.(theme) ?? TONE_COLORS.neutral(theme);
    return css`
      background: ${palette.dot};
    `;
  }}
`;

export const TrailingIcon = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: -2px;
  opacity: 0.85;
`;

export const LeadingIcon = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const Label = styled.span`
  white-space: nowrap;
`;
